import { Suspense, lazy, useEffect } from "react";
import { Route, Routes } from "react-router-dom";

import { Layout } from "./components/Layout";
import { Shell } from "./components/Shell";
import { Loading } from "./components/States";
import { prewarm } from "./lib/api";
import { Contact } from "./pages/Contact";
import { Experience } from "./pages/Experience";
import { Home } from "./pages/Home";
import { Placeholder } from "./pages/Placeholder";
import { Projects } from "./pages/Projects";

// The only two routes that render markdown, and react-markdown is ~50 kB
// gzipped — a third of DESIGN-BRIEF §2's whole 150 kB budget. Splitting them
// out keeps that weight off the homepage, which is the page the LCP target
// is actually about and the one a recruiter lands on.
const About = lazy(() =>
  import("./pages/About").then((m) => ({ default: m.About })),
);
const ProjectDetail = lazy(() =>
  import("./pages/ProjectDetail").then((m) => ({ default: m.ProjectDetail })),
);
// The admin is a separate audience and a separate bundle. A visitor reading
// the homepage should never download a dashboard they cannot log into.
const Admin = lazy(() => import("./pages/Admin").then((m) => ({ default: m.Admin })));

const Specimen = lazy(() =>
  import("./pages/Specimen").then((m) => ({ default: m.Specimen })),
);

export function App() {
  useEffect(() => {
    // PRD §11.2, and more so now that hosting is staying on free tiers: the
    // backend sleeps. Waking it on first paint means the cold start happens
    // while someone reads the hero rather than while they wait on a spinner.
    prewarm();
  }, []);

  return (
    <Suspense
      fallback={
        <Shell className="py-24">
          <Loading label="this page" />
        </Shell>
      }
    >
      <Routes>
        <Route path="admin" element={<Admin />} />

        <Route element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="projects" element={<Projects />} />
          <Route path="projects/:slug" element={<ProjectDetail />} />
          <Route path="experience" element={<Experience />} />
          <Route path="about" element={<About />} />
          {/* /architecture is gone. The Failure Path diagram, if it survives,
              belongs inside the chat area next to the system it describes —
              a page nobody navigates to is a page nobody reads. */}
          <Route
            path="chat-stats"
            element={
              <Placeholder
                title="Chat stats"
                note="Refusal rate, fallback rate and spend over the trailing 30 days. Real numbers or none."
              />
            }
          />
          <Route path="contact" element={<Contact />} />
          <Route path="specimen" element={<Specimen />} />
          <Route
            path="*"
            element={
              <Placeholder
                title="Nothing here"
                note="That page doesn't exist. The projects list is probably what you wanted."
              />
            }
          />
        </Route>
      </Routes>
    </Suspense>
  );
}
